import { create } from 'zustand';
import {
  DEFAULT_PLAYLIST_SORT_DIRECTION,
  DEFAULT_PLAYLIST_SORT_KEY,
} from '@/lib/constants/playlist';
import type { PlaylistSortKey } from '@/lib/types/Playlist';
import type { SearchMode } from '@/lib/types/Media';
import type { SortDirection } from '@/lib/types/common';

interface PlaylistGridState {
  searchText: string;
  searchMode: SearchMode;
  sortKey: PlaylistSortKey;
  sortDirection: SortDirection;
  scrollTop: number;
  awaitingBackNavPaint: boolean;
  orderedIdsSnapshot: number[];
  orderedIdsSignature: string | null;
  orderedIdsQueryKey: string | null;
}

interface PlaylistGridActions {
  setSearchText: (text: string) => void;
  setSearchMode: (mode: SearchMode) => void;
  setSort: (key: PlaylistSortKey) => void;
  setScrollTop: (top: number) => void;
  setOrderedIdsSnapshot: (signature: string, queryKey: string, ids: number[]) => void;
  markBackNavStart: () => void;
  completeBackNavPaint: () => void;
  reset: () => void;
}

const initialState: PlaylistGridState = {
  searchText: '',
  searchMode: 'fuzzy',
  sortKey: DEFAULT_PLAYLIST_SORT_KEY,
  sortDirection: DEFAULT_PLAYLIST_SORT_DIRECTION,
  scrollTop: 0,
  awaitingBackNavPaint: false,
  orderedIdsSnapshot: [],
  orderedIdsSignature: null,
  orderedIdsQueryKey: null,
};

export const usePlaylistGridState = create<PlaylistGridState & PlaylistGridActions>()((set) => ({
  ...initialState,

  setSearchText: (text) => set({ searchText: text }),

  setSearchMode: (mode) => set({ searchMode: mode }),

  setSort: (key) =>
    set((state) => {
      if (state.sortKey === key) {
        return {
          sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        sortKey: key,
        sortDirection: 'desc',
      };
    }),

  setScrollTop: (top) =>
    set((state) => {
      if (Math.abs(state.scrollTop - top) < 1) return state;
      return { scrollTop: top };
    }),

  setOrderedIdsSnapshot: (signature, queryKey, ids) =>
    set((state) => {
      if (
        state.orderedIdsSignature === signature &&
        state.orderedIdsQueryKey === queryKey &&
        state.orderedIdsSnapshot === ids
      ) {
        return state;
      }

      return {
        orderedIdsSignature: signature,
        orderedIdsQueryKey: queryKey,
        orderedIdsSnapshot: ids,
      };
    }),

  markBackNavStart: () => set({ awaitingBackNavPaint: true }),

  completeBackNavPaint: () => set({ awaitingBackNavPaint: false }),

  reset: () => set(initialState),
}));
